import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, CircularProgress, Collapse, List, ListItemButton, ListItemText } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import AdminNodeStore from '../../stores/admin-node-store';
import { DbNode } from '../../types/node';

export interface NodePickerItemProps {
    node: DbNode;
    depth: number;
    selectedNodeId: number | null;
    expandedIds?: Set<number>;
    onSelect: (node: DbNode) => void;
}

const NodePickerItem = observer(({ node, depth, selectedNodeId, expandedIds, onSelect }: NodePickerItemProps) => {
    const [store] = useState(() => new AdminNodeStore(node.node_id));
    const [expanded, setExpanded] = useState(() => expandedIds?.has(node.node_id) ?? false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        store.loadNode()
            .then(() => setLoaded(true))
            .catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleToggle = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!loaded) {
            await store.loadNode().catch(() => {});
            setLoaded(true);
        }
        setExpanded((v) => !v);
    };

    const isLeaf = loaded && store.items.length === 0;

    return (
        <>
            <ListItemButton
                selected={selectedNodeId === node.node_id}
                onClick={() => onSelect(node)}
                sx={{ pl: 1 + depth * 2, py: 0.5, borderRadius: 1 }}
            >
                <Box
                    sx={{ width: 20, flexShrink: 0, display: 'flex', alignItems: 'center', color: 'text.secondary' }}
                    onClick={isLeaf ? undefined : handleToggle}
                >
                    {store.loading ? (
                        <CircularProgress size={14} />
                    ) : isLeaf ? null : expanded ? (
                        <ExpandMoreIcon fontSize="small" />
                    ) : (
                        <ChevronRightIcon fontSize="small" />
                    )}
                </Box>
                <ListItemText primary={node.name} slotProps={{ primary: { variant: 'body2', noWrap: true } }} />
            </ListItemButton>
            <Collapse in={expanded} unmountOnExit>
                <List dense disablePadding>
                    {store.items.map((child) => (
                        <NodePickerItem
                            key={child.node_id}
                            node={child}
                            depth={depth + 1}
                            selectedNodeId={selectedNodeId}
                            expandedIds={expandedIds}
                            onSelect={onSelect}
                        />
                    ))}
                </List>
            </Collapse>
        </>
    );
});

export default NodePickerItem;
